import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Icon } from "@iconify/react/dist/iconify.js";
import { Modal, Button } from "react-bootstrap";
import TablePagination from "./TablePagination";
import { getFlashNews, deleteFlashNews } from "../Apis/FlashNewsAPIs";
import { formatDateUTC } from "../helper/DateHelper";
import usePermissions from "../hook/userPermission";
import { toast } from "react-toastify";

const FlashNewsListLayer = () => {
  const { hasPermission } = usePermissions();
  const navigate = useNavigate();

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [totalRecords, setTotalRecords] = useState(0);

  // Modal State
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [isUpdating, setIsUpdating] = useState(false);

  const fetchFlashNews = async () => {
    setLoading(true);
    const res = await getFlashNews({
      page: currentPage,
      limit: rowsPerPage,
      search: searchTerm,
    });
    if (res.status) {
      setData(res.response?.data || res.response || []);
      setTotalRecords(
        res.response?.total ||
        (res.response?.data || res.response || []).length,
      );
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchFlashNews();
  }, [currentPage, rowsPerPage, searchTerm]);

  const handleEdit = (item) => {
    navigate(`/flash-news/edit/${item._id || item.id}`);
  };

  const handleDeleteClick = (item) => {
    setItemToDelete(item);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!itemToDelete) return;
    setIsUpdating(true);
    const res = await deleteFlashNews(itemToDelete._id || itemToDelete.id);
    if (res.status) {
      toast.success("Flash news deleted successfully");
      fetchFlashNews();
      setShowDeleteModal(false);
      setItemToDelete(null);
    }
    setIsUpdating(false);
  };

  return (
    <div className="card h-100 p-0 radius-12">
      <div
        className="card-header border-bottom py-16 px-24 d-flex align-items-center justify-content-between"
        style={{
          background:
            "linear-gradient(135deg, #000000ff, #bb0007ff, #ff0000ff)",
        }}
      >
        <h6 className="text-white mb-0" style={{ fontWeight: "700" }}>
          Flash News
        </h6>
        <div className="d-flex align-items-center gap-3">
          <form className="navbar-search" onSubmit={(e) => e.preventDefault()}>
            <div className="position-relative">
              <input
                type="text"
                className="bg-base h-40-px w-auto radius-8 shadow-sm border-0"
                placeholder="Search news..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(0);
                }}
                style={{ paddingLeft: "40px" }}
              />
              <Icon
                icon="ion:search-outline"
                className="position-absolute text-secondary-light text-xl"
                style={{
                  left: "12px",
                  top: "50%",
                  transform: "translateY(-50%)",
                }}
              />
            </div>
          </form>
          {hasPermission("flash_news", "add") && (
            <Link
              to="/flash-news/add"
              className="btn btn-sm d-flex align-items-center gap-2"
              style={{
                backgroundColor: "#f7e7da",
                color: "black",
                fontWeight: "600",
                height: "40px",
              }}
            >
              <Icon icon="ic:baseline-plus" className="text-xl" />
              Add News
            </Link>
          )}
        </div>
      </div>

      <div className="card-body p-24">
        <div className="table-responsive scroll-sm">
          <table className="table bordered-table sm-table mb-0">
            <thead>
              <tr style={{ backgroundColor: "#f7e7da" }}>
                <th scope="col" style={{ color: "black", fontWeight: "bold" }}>
                  S.No
                </th>
                <th scope="col" style={{ color: "black", fontWeight: "bold" }}>
                  Name
                </th>
                <th scope="col" style={{ color: "black", fontWeight: "bold" }}>
                  Title
                </th>
                <th scope="col" style={{ color: "black", fontWeight: "bold" }}>
                  Description
                </th>
                <th scope="col" style={{ color: "black", fontWeight: "bold" }}>
                  Start Date
                </th>
                <th scope="col" style={{ color: "black", fontWeight: "bold" }}>
                  End Date
                </th>
                <th
                  scope="col"
                  className="text-center"
                  style={{ color: "black", fontWeight: "bold" }}
                >
                  Action
                </th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="7" className="text-center py-24">
                    Loading...
                  </td>
                </tr>
              ) : data.length > 0 ? (
                data.map((item, index) => (
                  <tr key={item._id || item.id}>
                    <td>{currentPage * rowsPerPage + index + 1}</td>
                    <td>{item.name}</td>
                    <td >{item.title}</td>
                    <td
                      className="text-truncate"
                      style={{ maxWidth: "200px" }}
                      title={item.description}
                    >
                      {item.description}
                    </td>
                    <td>{formatDateUTC(item.startDate)}</td>
                    <td>{formatDateUTC(item.endDate)}</td>
                    <td className="text-center">
                      <div className="d-flex align-items-center gap-10 justify-content-center">
                        {hasPermission("flash_news", "edit") && (
                          <button
                            type="button"
                            onClick={() => handleEdit(item)}
                            className="btn-edit-custom fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                          >
                            <Icon icon="lucide:edit" className="menu-icon" />
                          </button>
                        )}
                        {hasPermission("flash_news", "delete") && (
                          <button
                            type="button"
                            onClick={() => handleDeleteClick(item)}
                            className="bg-danger-focus text-danger-600 bg-hover-danger-200 w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle border-0"
                          >
                            <Icon icon="lucide:trash-2" className="text-xl" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="7" className="text-center py-24">
                    No news found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <TablePagination
          currentPage={currentPage}
          totalPages={Math.ceil(totalRecords / rowsPerPage) || 1}
          onPageChange={setCurrentPage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value));
            setCurrentPage(0);
          }}
          totalRecords={totalRecords}
        />
      </div>

      <Modal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        centered
      >
        <Modal.Body className="text-center p-5">
          <div className="d-flex justify-content-center mb-3">
            <div className="bg-danger-focus rounded-circle d-flex justify-content-center align-items-center w-64-px h-64-px">
              <Icon
                icon="lucide:trash-2"
                className="text-danger-600 text-xxl"
              />
            </div>
          </div>
          <h6>Are you sure you want to delete this news?</h6>
          <div className="d-flex justify-content-center gap-3 mt-4">
            <button
              type="button"
              className="btn btn-outline-secondary px-32 radius-8"
              onClick={() => setShowDeleteModal(false)}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn px-32 radius-8"
              onClick={confirmDelete}
              disabled={isUpdating}
              style={{
                backgroundColor: "#ed1c24",
                color: "white",
                borderColor: "#ed1c24",
              }}
            >
              {isUpdating ? "Processing..." : "Confirm"}
            </button>
          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default FlashNewsListLayer;
